import { useState, useMemo } from 'react';
import { Database, ExternalLink } from 'lucide-react';
import { InfoCard } from '@/entities/widget';
import { isValidUrl } from '@/entities/widget/lib';

import { Tabs, TabsList, TabsTrigger, Button } from '@monksflow/monks-ui';
import { getPlatformConfig } from '@/shared/constants';
import { OptimizedImage } from '@/shared/ui/OptimizedImage';
import { Globe } from 'lucide-react';

import type { TrendSummaryProps } from './TrendSummary.types';
import { useTranslation } from 'react-i18next';

const INITIAL_POSTS_DISPLAY = 4;

// Filter sentinel, not copy: it is the Tabs value, the test id suffix and the comparison below.
// eslint-disable-next-line i18next/no-literal-string
const ALL_PLATFORMS = 'All';

const capitalizePlatform = (platform: string): string => {
  /* eslint-disable i18next/no-literal-string -- social platform names */
  const platformMap: Record<string, string> = {
    tiktok: 'TikTok',
    youtube: 'Youtube',
    reddit: 'Reddit',
    x: 'X',
  };
  /* eslint-enable i18next/no-literal-string */
  return (
    platformMap[platform.toLowerCase()] || platform.charAt(0).toUpperCase() + platform.slice(1)
  );
};

export const BrandMentionsSection = ({ data, exporting = false }: TrendSummaryProps) => {
  const { t } = useTranslation('analysis');

  const [selectedPlatform, setSelectedPlatform] = useState<string>(ALL_PLATFORMS);
  const [showAll, setShowAll] = useState(false);

  const allSourcesWithPosts = useMemo(() => {
    return Object.values(data.sources).filter(
      (source): source is typeof source & { post: NonNullable<typeof source.post> } =>
        source.post !== undefined,
    );
  }, [data.sources]);

  const availablePlatforms = useMemo(() => {
    const platformsSet = new Set(
      allSourcesWithPosts.map((source) => source.post.source.toLowerCase()),
    );

    return [ALL_PLATFORMS, ...Array.from(platformsSet).map(capitalizePlatform).sort()];
  }, [allSourcesWithPosts]);

  const filteredSources = useMemo(() => {
    if (selectedPlatform === ALL_PLATFORMS) return allSourcesWithPosts;
    const normalizedPlatform = selectedPlatform.toLowerCase();
    return allSourcesWithPosts.filter(
      (source) => source.post.source.toLowerCase() === normalizedPlatform,
    );
  }, [allSourcesWithPosts, selectedPlatform]);

  const displayedSources = useMemo(() => {
    // Export view: render every source, unfiltered (the filter UI is hidden below).
    if (exporting) return allSourcesWithPosts;
    return showAll ? filteredSources : filteredSources.slice(0, INITIAL_POSTS_DISPLAY);
  }, [exporting, allSourcesWithPosts, filteredSources, showAll]);

  const hasTopVoices = data.topVoices.length > 0;
  const hasPosts = allSourcesWithPosts.length > 0;

  if (!hasTopVoices && !hasPosts) {
    return null;
  }

  return (
    <div className="flex flex-col rounded-2xl gap-6 mt-12">
      <div className="flex flex-row gap-4 items-center">
        <div
          className="flex flex-col w-13.5 h-13.5 items-center justify-center rounded bg-surface dark:bg-surface-raised"
          data-testid="sources-icon"
        >
          <Database size={24} />
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-2xl text-foreground font-medium">
            {t('socialMediaOpportunitiesWidget.brandMentionsSection.sources')}
          </p>
          <p className="text-base text-foreground-alt">
            {t('socialMediaOpportunitiesWidget.brandMentionsSection.dataSourcesThatInformedThis')}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-6" data-testid="brand-mentions-section">
          {/* Platform Filter — hidden in the export view (all sources shown). */}
          {!exporting && availablePlatforms.length > 1 && (
            <Tabs
              value={selectedPlatform}
              onValueChange={setSelectedPlatform}
              data-testid="platform-filter"
            >
              <TabsList>
                {availablePlatforms.map((platform) => (
                  <TabsTrigger
                    key={platform}
                    value={platform}
                    data-testid={`platform-button-${platform.toLowerCase()}`}
                  >
                    {platform === ALL_PLATFORMS
                      ? t('socialMediaOpportunitiesWidget.opportunitiesFilter.all')
                      : platform}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          )}

          {/* Posts Grid */}
          <div className="grid gap-4" data-testid="posts-grid">
            {displayedSources.map((source) => {
              const platformConfig = getPlatformConfig(source.post.source);

              return (
                <div key={source.key} className="relative group">
                  <InfoCard style="w-full p-6 gap-3" data-testid="post-card">
                    <div className="flex flex-row gap-2 items-center mb-4">
                      <div className="w-8 h-8 rounded-full bg-[#F5F5F4] dark:bg-neutral-50 flex items-center justify-center">
                        {platformConfig ? (
                          <OptimizedImage
                            src={platformConfig.image}
                            alt={source.post.source}
                            width={32}
                            height={32}
                          />
                        ) : (
                          <Globe size={32} />
                        )}
                      </div>
                      <div className="flex flex-col gap-0">
                        <p className="text-xs text-muted-foreground">
                          {capitalizePlatform(source.post.source)}
                        </p>
                        <div className="text-sm text-foreground font-medium">
                          {source.post.published_by_name}
                        </div>
                      </div>
                    </div>

                    <p className="text-base text-foreground-alt break-words ">
                      {source.post.description || source.post.postAnalysis?.summary}
                    </p>
                  </InfoCard>

                  {/* View Source — a real <a href> (via Button asChild) so it's
                      a genuine link: opens the source in-app AND becomes a
                      clickable link when the page is printed to PDF (an onClick
                      handler would not survive printing). */}
                  {source.url && isValidUrl(source.url) && (
                    <Button
                      asChild
                      variant="secondary"
                      size="sm"
                      className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid="view-source-button"
                    >
                      <a href={source.url} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="size-3.5" />
                        <span>
                          {t('socialMediaOpportunitiesWidget.brandMentionsSection.viewSource')}
                        </span>
                      </a>
                    </Button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pagination Button — not shown in the export view (all rendered). */}
          {!exporting && filteredSources.length > INITIAL_POSTS_DISPLAY && !showAll && (
            <div className="flex justify-center w-full">
              <Button
                variant="outline"
                onClick={() => setShowAll(true)}
                data-testid="show-more-posts-button"
              >
                {t('socialMediaOpportunitiesWidget.brandMentionsSection.morePosts', {
                  remainingCount: filteredSources.length - INITIAL_POSTS_DISPLAY,
                })}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
